import { productPriceService } from '@/services/productPriceService'
import type { CmsProduct } from '@/types/cms/product'

type StrapiImageData = {
    data?:
        | Array<{ attributes?: { url?: string } }>
        | { attributes?: { url?: string } }
}

type StrapiProduct = {
    id: number
    attributes?: {
        name?: string
        description?: string
        slug?: string
        order?: number
        sku?: string
        images?: StrapiImageData
    }
}

type StrapiResponse<T> = {
    data: T[]
}

const CMS_BASE_URL = import.meta.env.VITE_STRAPI_RS || ''
const PRODUCT_ENDPOINT = '/api/products'

const buildImageUrl = (path?: string | null) => {
    if (!path) return null
    if (path.startsWith('http')) return path
    if (!CMS_BASE_URL) return path
    return `${CMS_BASE_URL}${path}`
}

const extractFirstImageUrl = (images?: StrapiImageData) => {
    if (!images?.data) return null
    if (Array.isArray(images.data)) {
        return buildImageUrl(images.data[0]?.attributes?.url)
    }
    return buildImageUrl(images.data.attributes?.url)
}

const buildProductsUrl = (categorySlug: string, regionId?: number) => {
    const url = new URL(PRODUCT_ENDPOINT, CMS_BASE_URL)
    const params = new URLSearchParams()

    params.append('fields[0]', 'name')
    params.append('fields[1]', 'description')
    params.append('fields[2]', 'slug')
    params.append('fields[3]', 'order')
    params.append('fields[4]', 'sku')
    params.append('populate[images]', '*')
    params.append('filters[category][slug][$eq]', categorySlug)
    if (regionId) {
        params.append('filters[region][id][$eq]', String(regionId))
    }

    url.search = params.toString()
    return url.toString()
}

const mapProduct = (product: StrapiProduct, price?: number): CmsProduct => {
    return {
        id: product.id,
        name: product.attributes?.name ?? '',
        slug: product.attributes?.slug ?? '',
        order: product.attributes?.order ?? 0,
        sku: product.attributes?.sku,
        imageUrl: extractFirstImageUrl(product.attributes?.images),
        price
    }
}

const getProducts = async (
    categorySlug: string,
    regionId?: number
): Promise<CmsProduct[]> => {
    if (!CMS_BASE_URL) {
        throw new Error('CMS base URL is not configured')
    }
    const response = await fetch(buildProductsUrl(categorySlug, regionId))
    if (!response.ok) {
        throw new Error('Failed to fetch products')
    }
    const data = (await response.json()) as StrapiResponse<StrapiProduct>
    const skus = data.data
        .map((product) => product.attributes?.sku)
        .filter((sku): sku is string => Boolean(sku))
    let prices: { sku: string; price: number }[] = []
    try {
        const priceResults = await productPriceService.getBulkPrices(skus)
        prices = priceResults.map((item) => ({
            sku: item.sku,
            price:
                item.discountPriceStatus === 1 && item.discountPrice
                    ? item.discountPrice
                    : item.price
        }))
    } catch {
        prices = []
    }
    return data.data.map((product) => {
        const sku = product.attributes?.sku
        const price = sku
            ? prices.find((item) => item.sku === sku)?.price
            : undefined
        return mapProduct(product, price)
    })
}

export const cmsProductService = {
    getProducts
}
